import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../../ui/Table';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Eye, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import type { TeacherStudentSummaryItem, SortField, SortDirection } from '../../../tutor/types/teacherTutorAnalytics';

interface TeacherTutorStudentsTableProps {
  students: TeacherStudentSummaryItem[];
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}

export const TeacherTutorStudentsTable: React.FC<TeacherTutorStudentsTableProps> = ({
  students,
  sortField,
  sortDirection,
  onSort,
}) => {
  const navigate = useNavigate();
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatLastActive = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    return `${Math.floor(diffInDays / 30)} months ago`;
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4" />;
    return sortDirection === 'asc' ?
      <ArrowUp className="w-4 h-4" /> :
      <ArrowDown className="w-4 h-4" />;
  };

  const getScoreColor = (score: number | null) => {
    if (score === null) return 'secondary';
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  if (students.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No students found with tutor activity.</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <button
                onClick={() => onSort('student_id')}
                className="flex items-center gap-2 hover:text-gray-900 dark:hover:text-white"
              >
                Student Name
                {getSortIcon('student_id')}
              </button>
            </TableHead>
            <TableHead>
              <button
                onClick={() => onSort('minutes')}
                className="flex items-center gap-2 hover:text-gray-900 dark:hover:text-white"
              >
                Minutes
                {getSortIcon('minutes')}
              </button>
            </TableHead>
            <TableHead>
              <button
                onClick={() => onSort('sessions')}
                className="flex items-center gap-2 hover:text-gray-900 dark:hover:text-white"
              >
                Sessions
                {getSortIcon('sessions')}
              </button>
            </TableHead>
            <TableHead>
              <button
                onClick={() => onSort('last_active')}
                className="flex items-center gap-2 hover:text-gray-900 dark:hover:text-white"
              >
                Last Active
                {getSortIcon('last_active')}
              </button>
            </TableHead>
            <TableHead>Speaking Level</TableHead>
            <TableHead>
              <button
                onClick={() => onSort('score')}
                className="flex items-center gap-2 hover:text-gray-900 dark:hover:text-white"
              >
                Score
                {getSortIcon('score')}
              </button>
            </TableHead>
            <TableHead>Last Lesson</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {students.map((student) => (
            <TableRow
              key={student.student_id}
              onClick={() => navigate(`/teacher/tutor-analytics/student/${student.student_id}`, {
                state: { student }
              })}
              className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
            >
              <TableCell className="font-medium">
                <div className="text-gray-900 dark:text-white">
                  {student.student_info.display_name}
                </div>
                <div className="text-sm text-gray-500">
                  {student.student_info.admission_no && `Admission: ${student.student_info.admission_no}`}
                </div>
              </TableCell>
              <TableCell>
                {student.summary.total_tutor_minutes > 0 ?
                  formatDuration(student.summary.total_tutor_minutes) :
                  '—'
                }
              </TableCell>
              <TableCell>
                {student.summary.sessions_count || '—'}
              </TableCell>
              <TableCell className="text-sm">
                {formatLastActive(student.summary.last_active_at)}
              </TableCell>
              <TableCell>
                {student.summary.latest_speaking_level ? (
                  <Badge variant="gray">
                    {student.summary.latest_speaking_level}
                  </Badge>
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell>
                {student.summary.latest_overall_score !== null ? (
                  <Badge variant={getScoreColor(student.summary.latest_overall_score)}>
                    {student.summary.latest_overall_score}%
                  </Badge>
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell className="text-sm">
                {student.summary.last_lesson_no ?
                  `Lesson ${student.summary.last_lesson_no}` :
                  student.summary.last_topic_label || '—'
                }
              </TableCell>
              <TableCell>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/teacher/tutor-analytics/student/${student.student_id}`, {
                      state: { student }
                    });
                  }}
                  className="flex items-center gap-1"
                >
                  <Eye className="w-4 h-4" />
                  View
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};